import { ThemeEvents } from '@theme/events';
import {
  getDiscountedPrices,
  isRegiosDiscount,
  getRegiosDiscountTitle,
  buildRegiosPdpInputs,
} from '@theme/regios-pricing';

/**
 * @param {string | undefined} value
 * @returns {boolean}
 */
function asBoolean(value) {
  return String(value || '').trim().toLowerCase() === 'true';
}

/**
 * @param {unknown} value
 * @returns {number | null}
 */
function asNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * @param {{ compare_at_price?: unknown, compareAtPrice?: unknown, price?: unknown }} variant
 * @returns {boolean}
 */
function hasVariantCompareAt(variant) {
  if (!variant) return false;

  const compareAtRaw = variant.compare_at_price ?? variant.compareAtPrice ?? null;
  if (compareAtRaw == null || compareAtRaw === '') return false;

  const compareAt = asNumber(compareAtRaw);
  const price = asNumber(variant.price);

  if (compareAt == null) return true;
  if (price == null) return compareAt > 0;

  return compareAt > price;
}

class ProductMediaBadge extends HTMLElement {
  constructor() {
    super();
    this.onVariantUpdate = this.onVariantUpdate.bind(this);
    this.regiosDiscountActive = false;
    this.regiosDiscountTitle = '';
    /** Guards against stale Regios results after a fast variant switch. */
    this.regiosToken = 0;
  }

  connectedCallback() {
    this.badgeLabel = this.querySelector('.product-media-badge__label');
    if (!(this.badgeLabel instanceof HTMLElement)) return;

    this.readInitialState();

    const eventScope = this.closest('.shopify-section, dialog, main') || document;
    this.eventScope = eventScope;
    this.eventScope.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);

    this.render();
    this.applyRegios(this.dataset.selectedVariantId);
  }

  disconnectedCallback() {
    this.eventScope?.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
  }

  readInitialState() {
    this.productId = String(this.dataset.productId || '');
    this.allVariantsOutOfStock = asBoolean(this.dataset.allVariantsOutOfStock);
    this.launchTimestamp = asNumber(this.dataset.launchTimestamp) || 0;
    this.promoMessage = String(this.dataset.promoMessage || '').trim();
    this.isNew = asBoolean(this.dataset.isNew);
    this.isBestSeller = asBoolean(this.dataset.isBestSeller);
    this.hasNewColors = asBoolean(this.dataset.hasNewColors);
    this.selectedVariantAvailable = this.dataset.selectedVariantAvailable
      ? asBoolean(this.dataset.selectedVariantAvailable)
      : null;
    this.selectedVariantHasCompareAt = asBoolean(this.dataset.selectedVariantCompareAt);
  }

  /**
   * @param {import('./events').VariantUpdateEvent} event
   */
  onVariantUpdate(event) {
    if (!event?.detail?.data) return;
    if (String(event.detail.data.productId || '') !== this.productId) return;

    const variant = event.detail.resource;

    if (!variant) {
      this.selectedVariantAvailable = null;
      this.selectedVariantHasCompareAt = false;
      this.regiosDiscountActive = false;
      this.regiosDiscountTitle = '';
      this.regiosToken++;
      this.render();
      return;
    }

    this.selectedVariantAvailable = variant.available !== false;
    this.selectedVariantHasCompareAt = hasVariantCompareAt(variant);
    this.render();
    this.applyRegios(variant.id);
  }

  resolveBadge() {
    if (this.allVariantsOutOfStock) {
      return { copy: Theme.translations.product_badge_sold_out, isPromo: false };
    }

    if (this.selectedVariantAvailable === false) {
      return { copy: Theme.translations.product_badge_sold_out, isPromo: false };
    }

    if (this.launchTimestamp > Math.floor(Date.now() / 1000)) {
      return { copy: Theme.translations.product_badge_coming_soon, isPromo: false };
    }

    if (this.promoMessage) {
      return { copy: this.promoMessage, isPromo: true };
    }

    if (this.regiosDiscountActive && this.regiosDiscountTitle) {
      return { copy: this.regiosDiscountTitle, isPromo: true };
    }

    if (this.selectedVariantHasCompareAt) {
      return { copy: Theme.translations.product_badge_sale, isPromo: true };
    }

    if (this.isNew) {
      return { copy: Theme.translations.product_badge_new, isPromo: false };
    }

    if (this.hasNewColors) {
      return { copy: Theme.translations.product_badge_new_colors, isPromo: false };
    }

    if (this.isBestSeller) {
      return { copy: Theme.translations.product_badge_best_seller, isPromo: false };
    }

    return null;
  }

  render() {
    if (!(this.badgeLabel instanceof HTMLElement)) return;

    const badge = this.resolveBadge();
    if (!badge?.copy) {
      this.badgeLabel.textContent = '';
      this.badgeLabel.classList.remove('product-media-badge__label--promo');
      this.classList.remove('is-visible');
      this.setAttribute('hidden', '');
      return;
    }

    this.badgeLabel.textContent = badge.copy;
    this.badgeLabel.classList.toggle('product-media-badge__label--promo', Boolean(badge.isPromo));
    this.classList.add('is-visible');
    this.removeAttribute('hidden');
  }

  /**
   * @param {string|number|null|undefined} variantId
   */
  async applyRegios(variantId) {
    const token = ++this.regiosToken;
    const result = await getDiscountedPrices(
      buildRegiosPdpInputs(this.productId, variantId)
    );
    if (token !== this.regiosToken) return;

    this.regiosDiscountActive = !!result && isRegiosDiscount(result);
    this.regiosDiscountTitle = this.regiosDiscountActive
      ? getRegiosDiscountTitle(result)
      : '';
    this.render();
  }
}

if (!customElements.get('product-media-badge')) {
  customElements.define('product-media-badge', ProductMediaBadge);
}
